<script>
import Switch from "@/components/Shared/Device/Settings/Switch";
import Select from "@/components/Shared/Device/Settings/Select";
import Slider from "@/components/Shared/Device/Settings/Slider";
import Text from "@/components/Shared/Device/Settings/TextInput";
import Number from "@/components/Shared/Device/Settings/NumberInput";
import DateRange from "@/components/Shared/Device/Settings/DateRange";
import MotionSchedules from "@/components/Shared/Device/Settings/MotionSchedules";
import MotionZones from "@/components/Shared/Device/Settings/MotionZones";
import ComputerVision from "@/components/Shared/Device/Settings/ComputerVision";
import PrivacyZones from "@/components/Shared/Device/Settings/PrivacyZones";
import AerialZones from "@/components/Shared/Device/Settings/AerialZones";
import { initializeSettings } from "./helpers";
import { get, cloneDeep } from "lodash";

export default {
  name: "RingProductSettings",
  props: {
    device: {
      type: Object,
      required: true,
    },
    onlyApp: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      UIMetaSettings: [],
    };
  },
  watch: {
    device: function (device) {
      if (device.hasOwnProperty("type")) this.initialize(device);
    },
    onlyApp: function (show) {
      this.initialize(this.device);
      // this.$forceUpdate();
    },
  },
  created() {
    this.initialize(this.device);
  },
  updated() {
    const contentClass = ".el-card__body";
    let grid = this.$refs.ringDeviceSettings;
    const resizeGridItem = (item) => {
      let rowHeight = parseInt(
        window.getComputedStyle(grid).getPropertyValue("grid-auto-rows")
      );
      let rowGap = parseInt(
        window.getComputedStyle(grid).getPropertyValue("grid-row-gap")
      );
      let rowSpan = Math.ceil(
        (item.querySelector(contentClass).getBoundingClientRect().height +
          rowGap) /
          (rowHeight + rowGap)
      );
      item.style.gridRowEnd = "span " + rowSpan;
    };

    let resizeAllGridItems = () => {
      let allItems = grid.children;
      for (let x = 0; x < allItems.length; x++) {
        resizeGridItem(allItems[x]);
      }
    };

    resizeAllGridItems();
  },
  mounted() {},
  methods: {
    initialize(device) {
      initializeSettings(device).then((result) => {
        this.UIMetaSettings = result;
      });
    },
    onUpdateSetting({ payload, done }) {
      let data = cloneDeep(payload);
      let key = `${this.device.type}_firmware_settings`;
      let firmwareSettings = data[key];
      data["firmware_settings"] = firmwareSettings;
      delete data[key];
      this.$emit("change", { payload: data, done });
    },
  },
  render(h) {
    // Decide which UI control will be rendered
    const UIControl = (deviceSetting) => {
      let setting = deviceSetting;
      if (this.onlyApp) {
        let shouldCreateControl =
          get(setting, "show.app", false) || setting.hasOwnProperty("header");
        if (!shouldCreateControl) return null;
      }
      switch (setting.ui) {
        case "switch":
          return <Switch config={setting} on-change={this.onUpdateSetting} />;
        case "select":
          return <Select config={setting} on-change={this.onUpdateSetting} />;
        case "slider":
          return <Slider config={setting} on-change={this.onUpdateSetting} />;
        case "text":
          return <Text config={setting} on-change={this.onUpdateSetting} />;
        case "number":
          return <Number config={setting} on-change={this.onUpdateSetting} />;
        case "date-range":
          return (
            <DateRange config={setting} on-change={this.onUpdateSetting} />
          );
        case "motion-schedules":
          return <MotionSchedules />;
        case "motion-zones":
          return <MotionZones config={setting} />;
        case "privacy-zones":
          return <PrivacyZones config={setting} />;
        case "aerial-zones":
          return <AerialZones config={setting} />;
        case "computer-vision":
          return (
            <ComputerVision
              config={setting}
              paidFeatures={get(
                this.device,
                "backend_settings.paid_features",
                {}
              )}
            />
          );
        case "header":
          return h(`h${setting.header.level + 1}`, setting.header.text);
        default:
          console.error(`${setting.ui} UI control does not exist!`, setting);

          return null; // Throw Error
      }
    };

    let removeEmptyHeaders = (elements) => {
      const H2 = "h3";
      let result = [];
      let len = elements.length;
      for (let i = 0; i < len; i++) {
        let current = get(elements[i], "tag", {});
        let next = get(elements[i + 1], "tag", {});
        if (current === H2 && next === H2) {
          // ignore
        } else {
          result.push(elements[i]);
        }
      }
      let lastIndex = result.length - 1;
      if (get(result[lastIndex], "tag", null) === H2) result.pop();
      return result;
    };

    const buildSettingsDOMTree = (uiMetaSettings) => {
      if (!uiMetaSettings) return;
      let virtualDOMElements = [];
      let addDOMElement = (el) => {
        if (el !== null) {
          virtualDOMElements.push(el);
        }
      };
      let containerSections = [];
      let lastItemIndex = uiMetaSettings.length - 1;
      let createContainerFor = (virtualDOMElements) => {
        let children = removeEmptyHeaders(virtualDOMElements);
        if (children.length < 2) return;
        // let children = virtualDOMElements;
        containerSections.push(
          <el-card class="item" shadow="hover">
            {children}
          </el-card>
        );
      };
      uiMetaSettings.forEach((meta, currentIndex) => {
        let isHeaderLevel1 = () => get(meta, "header.level", -1) === 1;
        let isSettingList = () =>
          get(meta, "path", false) && get(meta, "fields", false);
        let shouldEncloseCurrentElements = () =>
          isHeaderLevel1() && virtualDOMElements.length > 0;

        if (isSettingList("?")) {
          Object.keys(meta.fields).forEach((key) => {
            let setting = meta.fields[key];
            addDOMElement(UIControl(setting));
          });
        } else if (shouldEncloseCurrentElements("?")) {
          createContainerFor(virtualDOMElements);
          virtualDOMElements = [UIControl(meta)]; // Start collecting elements for a new Container.
        } else {
          addDOMElement(UIControl(meta));
        }

        if (currentIndex === lastItemIndex) {
          createContainerFor(virtualDOMElements);
        }
      });

      return containerSections;
    };

    return (
      <div class="card-content">
        <div
          class="device-settings__container ring-device-settings-grid"
          ref="ringDeviceSettings"
        >
          {buildSettingsDOMTree(this.UIMetaSettings)}
        </div>
      </div>
    );
  },
};
</script>
<style lang="scss">
.ring-device-settings-grid {
  display: grid;
  grid-gap: 10px;
  grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
  grid-auto-rows: 20px;
  .item {
    height: fit-content;
  }
}
</style>
